import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, State, Watch, writeTask } from '@stencil/core'
import { noop, throttle } from 'lodash-es'
import { defaults, PositionMatch, reposition } from 'nanopop'
import { ResizeObserver } from 'resize-observer'

import { Pointer } from '../pointer/pointer'

export type NanoPopPosition =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'top-middle'
  | 'top-end'
  | 'left-start'
  | 'left-middle'
  | 'left-end'
  | 'right-start'
  | 'right-middle'
  | 'right-end'
  | 'bottom-start'
  | 'bottom-middle'
  | 'bottom-end'

let activePoppers: Popper[] = []

// Single scroll listener for all the various poppers
window.addEventListener(
  'scroll',
  (e) => {
    // Gather active poppers that belong to the scrolled element and update them
    const target = e.target as HTMLElement
    activePoppers.filter((p) => target.contains(p.el)).map((p) => p.reposition())
  },
  { passive: true, capture: true }
)

/**
 * NanoPop's `reposition` method returns an abbreviated value like `tm` instead
 * of `top-middle` this helper simply converts it back, since we need the full
 * one for other things
 */
function matchToPosition(match: PositionMatch) {
  return [
    {
      t: 'top',
      r: 'right',
      b: 'bottom',
      l: 'left',
    }[match.charAt(0)],
    {
      s: 'start',
      m: 'middle',
      e: 'end',
    }[match.charAt(1)],
  ].join('-') as NanoPopPosition
}

/**
 * Invert the position so the pointer can point at the reference element
 */
function invertPosition(position: NanoPopPosition) {
  const [direction, variant] = position.split('-')
  return [
    {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[direction],
    variant,
  ].join('-') as NanoPopPosition
}

/**
 * Updates a pointer's appearance to make sure it points at the reference element
 */
function updatePointer(pointerEl: HTMLUiPointerElement, position: NanoPopPosition) {
  const [direction, variant] = position.split('-')
  // Adjusts the direction of the pointer based on direction
  pointerEl.direction = {
    top: 'down',
    right: 'left',
    bottom: 'up',
    left: 'right',
  }[direction] as Pointer['direction']
  // Adjusts the angle of the pointer based on variant
  pointerEl.pointPct = {
    start: 0,
    middle: 0.5,
    end: 1,
  }[variant]
}

const container = document.createElement('aside')
container.classList.add('fixed', 'z-menu', 'inset-0', 'pointer-events-none', 'overflow-hidden')
document.querySelector('.tw').appendChild(container)

@Component({
  tag: 'ui-popper',
})
export class Popper {
  @Element() hostEl: HTMLElement
  @Event() repositioned: EventEmitter<NanoPopPosition>

  @Prop() visible = false
  @Prop() position = defaults.position as NanoPopPosition
  @Prop() reference: HTMLElement
  @Prop() margin = 0
  @Prop({ mutable: true }) flipped: boolean
  @Prop({ mutable: true }) currentPosition: NanoPopPosition = this.position
  @Prop() matchWidth = false

  // These are named such because if you prefix `on` then stencil will warn
  // about this being an event.  But the event-based implementation doesn't work
  // because we want a direct pass-through of the prop so the
  // event.stopImmediatePropagation has the correct scope.
  @Prop() popperMouseEnter: (event: MouseEvent) => void = noop
  @Prop() popperMouseLeave: (event: MouseEvent) => void = noop
  @Prop() popperClick: (event: MouseEvent) => void = noop

  @Prop() inline = false

  @State() referenceVisible = true

  el: HTMLElement
  resizeObserver: ResizeObserver
  intersectionObserver: IntersectionObserver
  restoreActiveState: boolean
  pointerEl: HTMLUiPointerElement

  get active() {
    return this.visible && this.referenceVisible && this.resizeObserver
  }

  @Watch('reference')
  onReference() {
    if (!this.intersectionObserver) {
      return
    }
    this.intersectionObserver.disconnect()
    if (this.reference) {
      this.intersectionObserver.observe(this.reference)
    } else {
      this.intersectionObserver.observe(this.hostEl.parentElement)
    }
    this.onVisible()
  }

  @Watch('visible')
  @Watch('referenceVisible')
  onVisible() {
    if (this.active) {
      if (!this.inline) {
        container.appendChild(this.el)
      } else {
        this.el.style.visibility = 'visible'
      }
      // Add it back to the list of active poppers if active
      if (!activePoppers.includes(this)) {
        activePoppers.push(this)
      }
      // Observe the popper and all parents for resizing/repositioning
      for (let el: Element = this.el; el; el = el.parentElement) {
        this.resizeObserver.observe(el)
      }
      // Update positions
      this.reposition()
    } else {
      // Don't watch for resize while hidden
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
      }
      // Remove this popper from the list of active ones
      activePoppers = activePoppers.filter((p) => p !== this)
      if (!this.inline) {
        this.el?.remove()
      } else if (this.el) {
        this.el.style.visibility = 'hidden'
      }
    }
  }

  @Method()
  async reposition() {
    // Don't update if we're not active
    if (!this.active) {
      return
    }
    const reference = this.reference || this.hostEl.parentElement
    if (this.matchWidth) {
      this.el.style.width = reference.clientWidth + 'px'
    }
    // Position the popper
    const match = reposition(reference, this.el, {
      position: this.position,
      margin: this.margin,
    })
    if (!match) {
      // nanopop can return `null` if a suitable position is not found, if that
      // happens we have nothing more to do here
      return
    }
    const pos = matchToPosition(match)
    // If a pointer (arrow) is in use, position it as well
    if (this.pointerEl) {
      // Update the size/direction of the pointer
      updatePointer(this.pointerEl, pos)
      writeTask(() => {
        // Wait for the update then position it with a slight negative margin so there is no visible seam
        reposition(this.pointerEl.parentElement, this.pointerEl, {
          position: invertPosition(pos),
          margin: -2,
        })
      })
    }
    this.flipped = pos !== this.position
    this.currentPosition = pos
    this.repositioned.emit(pos)
  }

  componentShouldUpdate() {
    return false
  }

  componentDidUpdate() {
    this.reposition()
  }

  componentDidLoad() {
    this.resizeObserver = new ResizeObserver(
      throttle(() => {
        this.reposition()
      }, 1000 / 30)
    )
    // Hide/show popper based on reference element's visibility
    this.intersectionObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        this.referenceVisible = entries[0].isIntersecting
      },
      { threshold: 1 }
    )
    this.pointerEl = Array.from(this.el.children).find((el) => el.matches('ui-pointer')) as HTMLUiPointerElement
    if (this.pointerEl) {
      this.pointerEl.style.position = 'fixed'
    }
    this.onReference()
    this.onVisible()
  }

  connectedCallback() {
    this.onReference()
  }

  disconnectedCallback() {
    this.referenceVisible = false
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
    }
  }

  render() {
    return (
      <Host class='absolute'>
        <aside
          class='absolute pointer-events-auto'
          ref={(el) => (this.el = el)}
          onMouseEnter={this.popperMouseEnter}
          onMouseLeave={this.popperMouseLeave}
          onClick={this.popperClick}
        >
          <slot />
        </aside>
      </Host>
    )
  }
}
